import { useEffect, useRef } from 'react';

interface SwipeHandlers {
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** Minimum px travel to register as a swipe. Defaults to 100. */
  threshold?: number;
}

export function useSwipe<T extends HTMLElement>(handlers: SwipeHandlers) {
  const ref = useRef<T>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  // Always holds the latest handlers without triggering the effect
  const handlersRef = useRef<SwipeHandlers>(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      touchStart.current = { x: t.clientX, y: t.clientY };
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!touchStart.current) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStart.current.x;
      const dy = t.clientY - touchStart.current.y;
      const {
        threshold = 100,
        onSwipeUp,
        onSwipeDown,
        onSwipeLeft,
        onSwipeRight,
      } = handlersRef.current;
      touchStart.current = null;

      if (Math.abs(dx) > Math.abs(dy)) {
        if (Math.abs(dx) < threshold) return;
        if (dx > 0) onSwipeRight?.();
        else onSwipeLeft?.();
      } else {
        if (Math.abs(dy) < threshold) return;
        if (dy > 0) onSwipeDown?.();
        else onSwipeUp?.();
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []); // runs once — latest handlers are always read from handlersRef

  return ref;
}
